/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { deviceIntegrationsSetScheduleEnabled } from "../funcs/deviceIntegrationsSetScheduleEnabled.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { DeviceIntegrationScheduleState } from "../models/components/deviceintegrationschedulestate.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SetDeviceIntegrationScheduleEnabledRequest,
  SetDeviceIntegrationScheduleEnabledSecurity,
} from "../models/operations/setdeviceintegrationscheduleenabled.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SetDeviceIntegrationScheduleEnabledMutationVariables = {
  request: SetDeviceIntegrationScheduleEnabledRequest;
  security?: SetDeviceIntegrationScheduleEnabledSecurity | undefined;
  options?: RequestOptions;
};

export type SetDeviceIntegrationScheduleEnabledMutationData =
  DeviceIntegrationScheduleState;

export type SetDeviceIntegrationScheduleEnabledMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * setScheduleEnabled deviceIntegrations
 *
 * @remarks
 * Enable or disable one sync schedule of a provider's device integration. Disabling records user intent on the schedule; only re-enabling clears it — config saves do not.
 */
export function useSetDeviceIntegrationScheduleEnabledMutation(
  options?: MutationHookOptions<
    SetDeviceIntegrationScheduleEnabledMutationData,
    SetDeviceIntegrationScheduleEnabledMutationError,
    SetDeviceIntegrationScheduleEnabledMutationVariables
  >,
): UseMutationResult<
  SetDeviceIntegrationScheduleEnabledMutationData,
  SetDeviceIntegrationScheduleEnabledMutationError,
  SetDeviceIntegrationScheduleEnabledMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSetDeviceIntegrationScheduleEnabledMutation(client, options),
    ...options,
  });
}

export function mutationKeySetDeviceIntegrationScheduleEnabled(): MutationKey {
  return ["@gram/client", "deviceIntegrations", "setScheduleEnabled"];
}

export function buildSetDeviceIntegrationScheduleEnabledMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SetDeviceIntegrationScheduleEnabledMutationVariables,
  ) => Promise<SetDeviceIntegrationScheduleEnabledMutationData>;
} {
  return {
    mutationKey: mutationKeySetDeviceIntegrationScheduleEnabled(),
    mutationFn: function setDeviceIntegrationScheduleEnabledMutationFn({
      request,
      security,
      options,
    }): Promise<SetDeviceIntegrationScheduleEnabledMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(deviceIntegrationsSetScheduleEnabled(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
